import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, X } from 'lucide-react'
import { AreaIcon } from '@/src/renderer/components/icons/AreaIcon'
import { QuickNotesFilledIcon } from '@/src/renderer/components/icons'
import { useIntroTextOptions } from '@/src/renderer/components/common/IntroTip'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import { DISCOVER_STEPS, introStepText, useDiscoverCard, type DiscoverStepId } from '@/src/renderer/lib/intro'
import type { TabIcon } from '@/src/renderer/hooks/multiWindow'
import { useSidebarData } from '../StandardSidebar/SidebarDataContext'
import './DiscoverCard.scss'

export interface DiscoverCardProps {
  lifeSpace: string
  navigateActiveTab: (updates: { path: string; title: string; icon?: TabIcon }) => void
}

/**
 * **Entdecken** (#323, Variante G): unten in der Seitenleiste, nur das, was man in den Listen kaum sieht.
 * Ein Punkt hakt sich ab, sobald man es einmal getan hat (`noteIntroStep`). Ein Klick führt hin — Pinnwand
 * ins Beispielprojekt, Fokus und Belohnung zu einer Zeile — und `DiscoverTip` erklärt es dort.
 *
 * @example
 * <DiscoverCard lifeSpace="personal" navigateActiveTab={navigateActiveTab} />
 */
export function DiscoverCard({ lifeSpace, navigateActiveTab }: DiscoverCardProps) {
  const { t } = useTranslation()
  const options = useIntroTextOptions()
  const { visible, collapsed, discovered, setCollapsed, hide } = useDiscoverCard()
  const { spaceProjects, quickNotesHasEntries } = useSidebarData(lifeSpace)

  if (!visible) return null

  const done = DISCOVER_STEPS.filter(step => discovered.includes(step)).length
  const total = DISCOVER_STEPS.length
  const sample = spaceProjects?.find(project => project.importedFrom?.startsWith('intro:sample:') && !project.deletedAt)

  const goTo = (step: DiscoverStepId, item: HTMLElement) => {
    const openSample = () => {
      if (!sample) return false
      navigateActiveTab({ path: `/home/${lifeSpace}/project/${sample.id}`, title: sample.name, icon: AreaIcon })
      return true
    }
    if (step === 'board') openSample()
    if (step === 'focus' || step === 'reward') {
      // ▶ und Rechtsklick brauchen eine Zeile: erst Kurz notiert, sonst das Beispielprojekt.
      if (quickNotesHasEntries) navigateActiveTab({ path: `/home/${lifeSpace}/quick-notes`, title: t('sidebar.quickNotes'), icon: QuickNotesFilledIcon })
      else openSample()
    }
    dispatchAppEvent('intro-discover', { step, fallback: item })
  }

  return (
    <section className="discover-card" aria-label={t('intro.discover.title')}>
      <div className="discover-card__head">
        <button
          type="button"
          className="discover-card__toggle"
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('intro.discover.expand') : t('intro.discover.collapse')}
          onClick={() => setCollapsed(!collapsed)}
        >
          <span className="discover-card__title">{t('intro.discover.title')}</span>
          <span className="discover-card__count" aria-hidden="true">{done}/{total}</span>
          <ChevronDown size={14} className={`discover-card__chevron${collapsed ? ' discover-card__chevron--collapsed' : ''}`} aria-hidden="true" />
        </button>
        <button type="button" className="discover-card__hide" aria-label={t('intro.discover.hide')} onClick={hide}>
          <X size={14} />
        </button>
      </div>
      <progress className="discover-card__bar" value={done} max={total} aria-label={t('intro.discover.progress', { done, total })} />
      {!collapsed && (done === total ? (
        <p className="discover-card__done">
          {t('intro.discover.done')}{' '}
          <button type="button" className="discover-card__link" onClick={hide}>{t('intro.discover.hide')}</button>
        </p>
      ) : (
        <ul className="discover-card__items">
          {DISCOVER_STEPS.map(step => {
            const ticked = discovered.includes(step)
            return (
              <li key={step}>
                <button
                  type="button"
                  className={`discover-card__item${ticked ? ' discover-card__item--done' : ''}`}
                  onClick={event => goTo(step, event.currentTarget)}
                >
                  <span className="discover-card__check" aria-hidden="true">{ticked && <Check size={10} strokeWidth={3} />}</span>
                  {introStepText(t, step, 'title', options)}
                </button>
              </li>
            )
          })}
        </ul>
      ))}
    </section>
  )
}
